import { useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Alert, AlertDescription } from "./ui/alert";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Lock,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Shield,
} from "lucide-react";
import { authAPI } from "@/api/auth";

interface ResetPasswordProps {
  isOpen: boolean;
  onClose: () => void;
  token?: string;
  onSuccess?: () => void;
}

export function ResetPassword({ isOpen, onClose, token, onSuccess }: ResetPasswordProps) {
  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [showPasswords, setShowPasswords] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const validatePasswords = () => {
    const errors: string[] = [];

    if (!passwords.newPassword) {
      errors.push("New password is required");
    } else {
      if (passwords.newPassword.length < 8) {
        errors.push("Password must be at least 8 characters long");
      }
      if (passwords.newPassword.length > 30) {
        errors.push("Password must be no more than 30 characters long");
      }
      if (/[^A-Za-z\d@$!%*?&]/.test(passwords.newPassword)) {
        errors.push("Password can only contain letters, numbers, and the special characters @$!%*?&");
      }
      if (!/(?=.*[a-z])/.test(passwords.newPassword)) {
        errors.push("Password must contain at least one lowercase letter");
      }
      if (!/(?=.*[A-Z])/.test(passwords.newPassword)) {
        errors.push("Password must contain at least one uppercase letter");
      }
      if (!/(?=.*\d)/.test(passwords.newPassword)) {
        errors.push("Password must contain at least one number");
      }
      if (!/(?=.*[@$!%*?&])/.test(passwords.newPassword)) {
        errors.push("Password must contain at least one special character (@$!%*?&)");
      }
    }

    if (!passwords.confirmPassword) {
      errors.push("Please confirm your password");
    } else if (passwords.newPassword !== passwords.confirmPassword) {
      errors.push("Passwords do not match");
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePasswords()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (!token) {
        throw new Error('Invalid reset token');
      }

      const response = await authAPI.resetPassword({ 
        token, 
        newPassword: passwords.newPassword 
      });
      
      if (response.code === "200") {
        setIsSubmitting(false);
        setIsComplete(true);
        
        toast.success("Password reset successfully! You can now sign in with your new password.");
        
     
        setTimeout(() => {
          handleClose();
          if (onSuccess) {
            onSuccess();
          }
        }, 3000);
      } else {
        throw new Error(response.message || 'Failed to reset password');
      }
      
    } catch (error) {
      setIsSubmitting(false);
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset password. Please try again or request a new reset link.';
      setValidationErrors([errorMessage]);
   
      toast.error(errorMessage);
    }
  };

  const handleInputChange = (field: keyof typeof passwords, value: string) => {
    setPasswords(prev => ({ ...prev, [field]: value }));
 
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const togglePasswordVisibility = (field: keyof typeof showPasswords) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleClose = () => {
    setPasswords({ newPassword: "", confirmPassword: "" });
    setShowPasswords({ newPassword: false, confirmPassword: false });
    setValidationErrors([]);
    setIsComplete(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <Shield className="w-5 h-5 text-primary" />
            <span>Reset Your Password</span>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
       
          {validationErrors.length > 0 && (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <AlertDescription className="text-red-800">
                <ul className="list-disc list-inside space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {isComplete ? (
            <div className="text-center py-6">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Password Reset Successfully!</h3>
              <p className="text-muted-foreground mb-4">
                Your password has been updated. You can now sign in with your new password.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  Redirecting you to the sign in page...
                </p>
              </div>
            </div>
          ) : isSubmitting ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Resetting Password...</h3>
              <p className="text-muted-foreground">Please wait while we update your password.</p>
            </div>
          ) : (
            <div className="space-y-4">
          
              <div className="text-center">
                <p className="text-muted-foreground">
                  Create a new secure password for your account.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
             
                <div className="space-y-2">
                  <Label htmlFor="newPassword">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                    <Input
                      id="newPassword"
                      type={showPasswords.newPassword ? "text" : "password"}
                      placeholder="Enter your new password"
                      value={passwords.newPassword}
                      onChange={(e) => handleInputChange("newPassword", e.target.value)}
                      className="pl-10 pr-10 h-12 rounded-lg border-gray-200 focus:border-primary focus:ring-primary"
                      maxLength={128}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility("newPassword")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-muted-foreground"
                    >
                      {showPasswords.newPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

              
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                    <Input
                      id="confirmPassword"
                      type={showPasswords.confirmPassword ? "text" : "password"}
                      placeholder="Confirm your new password"
                      value={passwords.confirmPassword}
                      onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                      className="pl-10 pr-10 h-12 rounded-lg border-gray-200 focus:border-primary focus:ring-primary"
                      maxLength={128}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility("confirmPassword")}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-muted-foreground"
                    >
                      {showPasswords.confirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || !passwords.newPassword || !passwords.confirmPassword}
                  className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg shadow-lg shadow-primary/20 transition-all duration-200"
                >
                  {isSubmitting ? "Resetting..." : "Reset Password"}
                </Button>
              </form>

             
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <Shield className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs font-medium text-gray-700 mb-1">Password Requirements:</p>
                    <ul className="text-xs text-muted-foreground space-y-1">
                      <li>• At least 8 characters long</li>
                      <li>• One uppercase and one lowercase letter</li>
                      <li>• At least one number</li>
                      <li>• At least one special character (@$!%*?&)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isSubmitting && !isComplete && (
            <div className="flex justify-end pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="rounded-lg"
              >
                Cancel
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
